<template>
    <div class="ready-stage">
        <form
            ref="form"
            class="funnel-email-form"
        >
            <section>
                <header class="ready-stage-header">
                    <h4 class="stage-title">
                        {{ $t('unlayerBuilder.misc.setUpTheDetails') }}
                    </h4>
                </header>

                <div class="from-field">
                    <sender-address-field
                        v-model="emailTemplate.fromAddress"
                        class="sender-address-field"
                        :force-invalid="dkimError"
                        :environment="environment"
                        :label="$t('global.from')"
                        :options="senders"
                        :submitted="submitted"
                        :show-verified-domains-new-keys-only-enabled="showVerifiedDomainsNewKeysOnlyEnabled"
                        data-qa="sender-address-field"
                    />
                </div>

                <DkimVerificationAlert
                    class="dkim-verification-alert"
                    :sender-list="senderList"
                    :sender="emailTemplate.fromAddress"
                    :contact-owner-identifier="$options.DEFAULT_FROM_VALUE"
                    :show-verified-domains-new-keys-only-enabled="showVerifiedDomainsNewKeysOnlyEnabled"
                    :event-source="$options.AMPLITUDE_EVENT_SOURCES.UNLAYER_EMAIL_FUNNEL"
                    @validity="onSenderValidityChange"
                    @error-loading-domains="handleErrorLoadingDomains"
                />

                <div class="recipients-field">
                    <recipient-address-field
                        v-model="emailTemplate.toAddress"
                        :class="{ 'to-address-field': !dkimError && !dkimWarning }"
                        :submitted="submitted"
                    />
                </div>

                <div class="subject-field">
                    <AdvancedInput
                        v-model="emailTemplate.subject"
                        name="subject"
                        data-qa="subject-field-advanced"
                        :autocomplete="$options.SUBJECT_AUTOCOMPLETE"
                        :error-text="errorText"
                        :label="$t('unlayerBuilder.email.subjectInput.label')"
                        :maxlength="$options.SUBJECT_MAX_LENGTH"
                        required
                        :submitted="submitted"
                        @input="handleSubjectChanged"
                    >
                        <template #toolbar="{ insertTextIntoInput }">
                            <EmailBroadcastInputToolbar
                                :insert-text-into-input="insertTextIntoInput"
                                :merge-field-categories="mergeFields"
                            />
                        </template>
                    </AdvancedInput>

                    <content-risk-messages
                        v-if="subjectRiskMessages.length > 0 && subjectRiskQuality !== CONTENT_RISK_QUALITY.OK"
                        :collapsible="subjectRiskMessages.length > 1"
                        :quality="subjectRiskQuality"
                        :messages="subjectRiskMessages"
                        class="subject-risk-messages"
                        data-qa="subject-risk-messages"
                    >
                        <span>
                            {{ subjectRiskPrefixText }}
                            <strong>{{ subjectRiskStrongText }}</strong>
                            {{ subjectRiskSuffixText }}
                        </span>
                    </content-risk-messages>
                </div>

                <div class="preview-text-field">
                    <AdvancedInput
                        v-model="emailTemplate.previewText"
                        name="preview-text"
                        data-qa="preview-field-advanced"
                        :autocomplete="$options.PREVIEW_AUTOCOMPLETE"
                        :label="$t('unlayerBuilder.email.previewTextInput.label')"
                        :maxlength="$options.PREVIEW_MAX_LENGTH"
                        :help-text="$t('unlayerBuilder.email.previewTextInput.helpText')"
                    >
                        <template #toolbar="{ insertTextIntoInput }">
                            <EmailBroadcastInputToolbar
                                :insert-text-into-input="insertTextIntoInput"
                                :merge-field-categories="mergeFields"
                            />
                        </template>
                    </AdvancedInput>
                </div>
            </section>
        </form>

        <portal to="unlayer-email-editor-actions">
            <div class="header-button-row">
                <ds-toggle-switch
                    :value="ready"
                    :disabled="!contentRiskSendable || !subjectRiskSendable"
                    :label-on="$t('unlayerBuilder.readyToggle.ready')"
                    :label-off="$t('unlayerBuilder.readyToggle.draft')"
                    class="ready-toggle"
                    @input="$emit('update:ready', !ready)"
                />

                <ds-filled-button
                    :loading="savingEmailTemplate || loadingSubjectRisk"
                    data-qa="done-button"
                    @click="submit"
                >
                    {{ $t('global.done') }}
                </ds-filled-button>
            </div>
        </portal>
    </div>
</template>

<script>
import axios from 'axios';
import debounce from 'lodash.debounce';
import AdvancedInput from '@/components/shared/AdvancedInput.vue';
import EmailBroadcastInputToolbar from '@/components/broadcast/email/EmailBroadcastInputToolbar.vue';
import RecipientAddressField from '@/components/unlayer/RecipientAddressField';
import SenderAddressField from '@/components/unlayer/SenderAddressField';
import DkimVerificationAlert from '@/components/shared/DkimVerificationAlert';
import sentry from '@/analytics/sentry';


import ContentRiskMessages from './ContentRiskMessages';
import { CONTENT_RISK_QUALITY } from './content-risk.constants';
import { INPUT_DEBOUNCE_DELAY } from '@/components/shared/constants/timing.constants';
import { isValidEmailAddress } from '@/utils/email.util';
import {
    AMPLITUDE_EVENT_SOURCES,
    DEFAULT_FROM_VALUE,
    DEFAULT_RECIPIENT_VALUES,
    EMAIL_BROADCAST_SUBJECT,
    EMAIL_BROADCAST_PREVIEW,
} from '@/components/broadcast/constants/emailBroadcast.constants';

import { isSpaApp } from '@/api/shared';

import { trackToggleReadyClicksDone } from '@/components/broadcast/emailAnalytics';

export default {
    SUBJECT_MAX_LENGTH: EMAIL_BROADCAST_SUBJECT.maxlength,
    SUBJECT_AUTOCOMPLETE: EMAIL_BROADCAST_SUBJECT.autocomplete,
    PREVIEW_MAX_LENGTH: EMAIL_BROADCAST_PREVIEW.maxlength,
    PREVIEW_AUTOCOMPLETE: EMAIL_BROADCAST_PREVIEW.autocomplete,
    DEFAULT_FROM_VALUE,
    AMPLITUDE_EVENT_SOURCES,

    components: {
        ContentRiskMessages,
        AdvancedInput,
        EmailBroadcastInputToolbar,
        RecipientAddressField,
        SenderAddressField,
        DkimVerificationAlert,
    },

    props: {
        environment: String,

        emailTemplate: {
            type: Object,
            required: true,
        },

        mergeFields: {
            type: Array,
            required: true,
        },

        ready: {
            type: Boolean,
            sync: true,
        },

        users: {
            type: Array,
            required: true,
        },

        contentRiskSendable: Boolean,
        contentRiskFailed: Boolean,

        subjectRiskSendable: Boolean,
        loadingSubjectRisk: Boolean,
        funnelId: String,

        subjectRiskQuality: String,
        subjectRiskMessages: {
            type: Array,
            default: () => [],
        },

        fetchSubjectRisks: {
            type: Function,
            default: (x) => x,
        },


        showVerifiedDomainsNewKeysOnlyEnabled: Boolean,
    },

    data() {
        return {
            submitted: false,
            savingEmailTemplate: false,
            CONTENT_RISK_QUALITY,
            dkimError: false,
            dkimWarning: false,
        };
    },

    created() {
        if (this.contentRiskFailed) {
            this.$error({
                message: 'Something went wrong while checking your email content for spam issues. Go back to try again.',
                bottom: true,
            });
        }

        this.handleSubjectChanged(); // Do a content risk check when component loads

        this.handleSubjectChanged = debounce(this.handleSubjectChanged, INPUT_DEBOUNCE_DELAY);
    },

    computed: {
        senders() {
            const users = this.users || [];

            return [
                { label: this.$t('unlayerBuilder.emailSender.contactOwner'), value: DEFAULT_FROM_VALUE },
                ...users
                    .filter((u) => !!u.fullName?.trim().length && isValidEmailAddress(u.email))
                    .sort((u1, u2) => u1.fullName.trim().localeCompare(u2.fullName.trim()) || u1.email.localeCompare(u2.email))
                    .map(({ email, fullName }) => ({
                        label: `"${fullName.trim()}" <${email}>`,
                        value: `"${fullName}" <${email}>`,
                    })),
            ];
        },

        senderList() {
            return this.senders.map((sender) => {
                return (sender.value === DEFAULT_FROM_VALUE) ? sender.value : sender.label;
            });
        },

        toAddressFields() {
            return [
                { label: this.$t('unlayerBuilder.emailRecipient.label.email'), value: DEFAULT_RECIPIENT_VALUES.EMAIL },
                { label: this.$t('unlayerBuilder.emailRecipient.label.emailAddress2'), value: DEFAULT_RECIPIENT_VALUES.EMAIL2 },
                { label: this.$t('unlayerBuilder.emailRecipient.label.emailAddress3'), value: DEFAULT_RECIPIENT_VALUES.EMAIL3 },
            ];
        },

        subjectRiskPrefixText() {
            return this.subjectRiskMessages.length === 1
                ? 'There is'
                : 'There are';
        },

        subjectRiskStrongText() {
            return this.subjectRiskMessages.length === 1
                ? '1 word or phrase'
                : `${this.subjectRiskMessages.length} words or phrases`;
        },

        subjectRiskSuffixText() {
            const staticText = 'in your subject line that could cause email to be flagged as spam.';

            if (this.subjectRiskQuality === CONTENT_RISK_QUALITY.SPAM_NOT_SENDABLE) {
                return this.subjectRiskMessages.length === 1
                    ? `${staticText} It must be removed before sending your email.`
                    : `${staticText} These must be removed before sending your email.`;
            }

            return this.subjectRiskMessages.length === 1
                ? `${staticText} Consider removing it before sending your email.`
                : `${staticText} Consider removing them before sending your email.`;
        },

        errorText() {
            // return error text if subject is empty
            return this.emailTemplate.subject ? '' : this.$t('unlayerBuilder.email.subjectInput.error.subjectLineCannotBeEmpty');
        },
    },

    methods: {
        handleSubjectChanged() {
            const { subject } = this.emailTemplate;

            this.fetchSubjectRisks(subject);
        },

        async save() {
            this.savingEmailTemplate = true;

            const {
                id,
                htmlBody,
                design,
                trackedLinks,
                subject,
                previewText,
                toAddress,
                fromAddress,
            } = this.emailTemplate;

            try {
                await axios.put(`/app/unlayerEmailFunnel/template/${id}`, {
                    html: htmlBody,
                    design: JSON.stringify(design),
                    trackedLinks,
                    subject,
                    previewText,
                    toAddress,
                    fromAddress,
                    funnelId: this.funnelId,
                });
            } catch (error) {
                sentry.captureException(error);
                this.$error({
                    message: this.$t('email.broadcast.validated.templates.error.failedToSaveTemplate'),
                    bottom: true,
                });
            }

            this.savingEmailTemplate = false;
        },

        async submit() {
            this.submitted = true;

            if (this.dkimError) {
                return;
            }

            if (this.$refs.form.checkValidity()) {
                await this.save();
                const isSpa = await isSpaApp();

                trackToggleReadyClicksDone(isSpa);
                this.$emit('template-ready');
            }
        },

        onSenderValidityChange({ error, warning }) {
            this.dkimError = error;
            this.dkimWarning = warning;
        },

        handleErrorLoadingDomains() {
            this.$error({
                message: this.$t('email.broadcast.validated.domains.error.failedToLoadDomains'),
                bottom: true,
            });
        },
    },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
.ready-stage {
    padding: 0 $spacing-200;
    height: 100%;
    overflow: auto;
}

.funnel-email-form {
    max-width: px-to-rem(784);
    margin: 0 auto;
    padding-bottom: $spacing-600;
}

.ready-stage-header {
    padding: $spacing-300 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    position: relative;
}

.ready-toggle {
    @include margin-end($spacing-200);
    align-self: center;
}

.stage-title {
    font-weight: 700;
    text-align: center;
    flex: 1
}

.from-field,
.recipients-field,
.dkim-verification-alert {
    width: 50%;
}

.from-field {
    margin-bottom: $spacing-150;
}

.from-edit-icon {
    --icon-color: #{$color-gray-600};
}

.sender-address-field,
.from-address-field {
    --input-margin-bottom: 0.75rem;
}

.to-address-field {
    margin-top: 1.5rem;
}

.subject-risk-messages {
    margin: -$spacing-200 0 $spacing-300;
}
</style>
